import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import faEdit from '@fortawesome/fontawesome-free-solid/faEdit';
import faCheck from '@fortawesome/fontawesome-free-solid/faCheck';
import faTimes from '@fortawesome/fontawesome-free-solid/faTimes';
import { Field } from 'formik';
import { formatMoney } from 'accounting';
import { DateTime } from 'luxon';

import TypeHints from '../../../../config/typeHints';
import TextInput from '../../../common/form/TextInput';
import TextArea from '../../../common/form/TextArea';
import SelectInput from '../../../common/form/SelectInput';
import RadioSelect from '../../../common/form/RadioSelect';
import ContactPicker from '../../../common/form/ContactPicker';
import ContactLink from '../../../common/ContactLink';
import MonetaryInput from '../../../common/form/MonetaryInput';
import formPropsType from '../../../common/types/formPropsType';
import './EditableLineItem.css';

class EditableLineItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isEditing: props.startInEditMode,
      initialValue: props.value,
    };
  }

  componentDidUpdate(prevProps) {
    const {
      propertyPath,
      formProps: { touched },
    } = this.props;
    const {
      formProps: { touched: prevTouched },
    } = prevProps;

    if (_.get(prevTouched, propertyPath) && !_.get(touched, propertyPath)) {
      this.setState({ isEditing: false, initialValue: this.props.value });
    }
  }

  renderField = () => {
    const {
      attribute,
      propertyPath,
      formProps: { isSubmitting },
    } = this.props;

    let fieldType = 'text';
    let fieldComponent = TextInput;
    let options;

    switch (attribute.validations.type) {
      case 'decimal':
      case 'integer':
        fieldType = 'number';
        if (attribute.typeHint === TypeHints.Monetary) {
          fieldComponent = MonetaryInput;
        }
        break;
      case 'foreignKey':
        fieldType = 'number';
        fieldComponent = ContactPicker;
        break;
      case 'date':
        fieldType = 'date';
        break;
      case 'enum':
        fieldType = 'radio';
        fieldComponent =
          _.sumBy(_.values(attribute.validations.enumType), t => t.length) > 40 ||
          _.size(attribute.validations.enumType) > 3
            ? SelectInput
            : RadioSelect;
        options = _.map(attribute.validations.enumType, (v, k) => ({ label: v, value: k }));
        break;
      default:
        // Likely a string, but there may be a twist
        if (attribute.typeHint && attribute.typeHint === TypeHints.TextArea) {
          fieldComponent = TextArea;
        }
    }

    return (
      <Field
        name={propertyPath}
        type={fieldType}
        options={options}
        render={(props) => {
          const {
            // eslint-disable-next-line react/prop-types
            form: { handleSubmit, setFieldValue },
          } = props;
          const input = React.createElement(fieldComponent, { ...props, type: fieldType, options });

          return (
            <div style={{ flex: 1 }} className="field is-grouped">
              <div className="control is-expanded">{input}</div>
              <div className="editableValue-controls control buttons">
                <button
                  type="button"
                  className="editableValue-saveButton button is-small is-outlined is-success"
                  onClick={() => {
                    handleSubmit();
                  }}
                  disabled={isSubmitting}
                >
                  <FontAwesomeIcon icon={faCheck} className="icon" fixedWidth />
                </button>
                <button
                  type="button"
                  className="editableValue-cancelButton button is-small is-outlined is-warning"
                  onClick={() => {
                    setFieldValue(propertyPath, this.state.initialValue);
                    this.setState({ isEditing: false });
                  }}
                  disabled={isSubmitting}
                >
                  <FontAwesomeIcon icon={faTimes} className="icon" fixedWidth />
                </button>
              </div>
            </div>
          );
        }}
      />
    );
  };

  renderValue = () => {
    const { isEditing } = this.state;
    const { value, attribute } = this.props;

    if (isEditing) {
      return <dd className="editableValue editableValue--editing">{this.renderField()}</dd>;
    }

    let displayValue;

    switch (attribute.typeHint) {
      case TypeHints.Monetary:
        displayValue = formatMoney(value);
        break;
      case TypeHints.Date:
        displayValue = value && DateTime.fromISO(value).toLocaleString(DateTime.DATE_FULL);
        break;
      case TypeHints.Contact:
        displayValue = value ? <ContactLink contactId={value} /> : '';
        break;
      default:
        displayValue = value;
    }

    return (
      <dd className="editableValue">
        <button
          type="button"
          className="functionOnlyButton"
          onClick={() => {
            this.setState({ isEditing: true });
          }}
          style={{ width: '100%' }}
        >
          {displayValue || '\u00A0'}
          <div className="editableValue-controls">
            <FontAwesomeIcon icon={faEdit} fixedWidth />
          </div>
        </button>
      </dd>
    );
  };

  render() {
    const { label } = this.props;

    return (
      <React.Fragment>
        <dt>{label}</dt>
        {this.renderValue()}
      </React.Fragment>
    );
  }
}

EditableLineItem.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  attribute: PropTypes.shape({
    friendly: PropTypes.string,
    validations: PropTypes.object,
  }),
  propertyPath: PropTypes.string,
  startInEditMode: PropTypes.bool,
  formProps: formPropsType.isRequired,
};

EditableLineItem.defaultProps = {
  label: '',
  value: '',
  attribute: null,
  propertyPath: '',
  startInEditMode: false,
};

export default EditableLineItem;



// WEBPACK FOOTER //
// ./src/components/pages/collections/items/EditableLineItem.jsx